import type { AcceptedLanguages } from '@payloadcms/translations'
import type { ImportMap, LanguageOptions, SanitizedConfig, ServerFunctionClient } from 'payload'

import { rtlLanguages } from '@payloadcms/translations'
import { ProgressBar, RootProvider } from '@payloadcms/ui'
import { getClientConfig } from '@payloadcms/ui/utilities/getClientConfig'
import { cookies as nextCookies } from 'next/headers.js'
import { applyLocaleFiltering } from 'payload/shared'
import React, { Suspense } from 'react'

import { getNavPrefs } from '../../elements/Nav/getNavPrefs.js'
import { getRequestTheme } from '../../utilities/getRequestTheme.js'
import { initReq } from '../../utilities/initReq.js'
import { checkDependencies } from './checkDependencies.js'
import { NestProviders } from './NestProviders.js'

import '@payloadcms/ui/scss/app.scss'

export const metadata = {
  description: 'Generated by Next.js',
  title: 'Next.js',
}

type RootLayoutProps = {
  readonly children: React.ReactNode
  readonly config: Promise<SanitizedConfig>
  readonly htmlProps?: React.HtmlHTMLAttributes<HTMLHtmlElement>
  readonly importMap: ImportMap
  readonly serverFunction: ServerFunctionClient
}

export const RootLayout = ({
  children,
  config: configPromise,
  htmlProps,
  importMap,
  serverFunction,
}: RootLayoutProps) => {
  checkDependencies()

  const content = (
    <RootLayoutContent
      config={configPromise}
      htmlProps={htmlProps}
      importMap={importMap}
      serverFunction={serverFunction}
    >
      {children}
    </RootLayoutContent>
  )

  if (process.env.PAYLOAD_CACHE_COMPONENTS_ENABLED === 'true') {
    return <Suspense fallback={null}>{content}</Suspense>
  }

  return content
}

const RootLayoutContent = async ({
  children,
  config: configPromise,
  htmlProps = {},
  importMap,
  serverFunction,
}: RootLayoutProps) => {
  const {
    cookies,
    headers,
    languageCode,
    permissions,
    req,
    req: {
      payload: { config },
    },
    user: userWithReadAccess,
  } = await initReq({ configPromise, importMap, key: 'RootLayout' })

  const theme = getRequestTheme({
    config,
    cookies,
    headers,
  })

  const dir = (rtlLanguages as unknown as AcceptedLanguages[]).includes(languageCode)
    ? 'RTL'
    : 'LTR'

  const languageOptions: LanguageOptions = Object.entries(
    config.i18n.supportedLanguages || {},
  ).reduce((acc, [language, languageConfig]) => {
    if (Object.keys(config.i18n.supportedLanguages).includes(language)) {
      acc.push({
        label: languageConfig.translations.general.thisLanguage,
        value: language,
      })
    }

    return acc
  }, [])

  async function switchLanguageServerAction(lang: string): Promise<void> {
    'use server'
    const cookies = await nextCookies()
    cookies.set({
      name: `${config.cookiePrefix || 'payload'}-lng`,
      maxAge: 60 * 60 * 24 * 365,
      path: '/',
      value: lang,
    })
  }

  const navPrefs = await getNavPrefs(req)

  const clientConfig = getClientConfig({
    config,
    i18n: req.i18n,
    importMap,
    user: userWithReadAccess,
  })

  await applyLocaleFiltering({ clientConfig, config, req })

  return (
    <html
      data-theme={theme}
      dir={dir}
      lang={languageCode}
      suppressHydrationWarning={config?.admin?.suppressHydrationWarning ?? false}
      {...htmlProps}
    >
      <head>
        <style>{`@layer payload-default, payload;`}</style>
      </head>
      <body>
        <RootProvider
          config={clientConfig}
          dateFNSKey={req.i18n.dateFNSKey}
          fallbackLang={config.i18n.fallbackLanguage}
          isNavOpen={navPrefs?.open ?? true}
          languageCode={languageCode}
          languageOptions={languageOptions}
          locale={req.locale}
          permissions={userWithReadAccess ? permissions : null}
          serverFunction={serverFunction}
          switchLanguageServerAction={switchLanguageServerAction}
          theme={theme}
          translations={req.i18n.translations}
          user={userWithReadAccess}
        >
          <ProgressBar />
          {Array.isArray(config.admin?.components?.providers) &&
          config.admin?.components?.providers.length > 0 ? (
            <NestProviders
              importMap={req.payload.importMap}
              providers={config.admin?.components?.providers}
              serverProps={{
                i18n: req.i18n,
                payload: req.payload,
                permissions,
                user: userWithReadAccess,
              }}
            >
              {children}
            </NestProviders>
          ) : (
            children
          )}
        </RootProvider>
        <div id="portal" />
      </body>
    </html>
  )
}
